import { useMemo } from 'react';

import { useMy } from '@/hooks/useMy';
import type { DropdownOption } from '@/types/dropdown.types';
import { useMergeStatus } from '@/utils/api';

// Type for AD filter options - comprehensive AD properties
type ADFilterOptions = {
  domain: DropdownOption[];
  status: DropdownOption[];
  source: DropdownOption[];
  class: DropdownOption[];
  samaccountname: DropdownOption[];
  groupscope: DropdownOption[];
};

export const useGetADFilters = (): {
  data: ADFilterOptions;
  status: 'idle' | 'pending' | 'success' | 'error';
} => {
  // Fetch AD-specific statistics for dropdown values
  // Note: 'adobject' query returns both domains (name="domains") and SAM accounts (name="sam")
  const { data: adObjectStatistics, status: adObjectStatus } = useMy({
    resource: 'statistic',
    query: 'adobject',
  });

  // Also fetch adcomputer#domain statistics as a fallback for domain data
  const { data: adComputerDomainStats, status: adComputerDomainStatus } = useMy(
    {
      resource: 'statistic',
      query: 'adcomputer#domain',
    }
  );

  // Try to fetch ADUser statistics which might have SAM accounts
  // Note: Based on backend query analysis, samaccountname should be on ADUser or ADComputer nodes
  const { data: adUserStats, status: adUserStatus } = useMy({
    resource: 'statistic',
    query: 'aduser#samaccountname',
  });

  // Also try fetching ADComputer stats which might have SAM accounts
  const { data: adComputerStats, status: adComputerStatus } = useMy({
    resource: 'statistic',
    query: 'adcomputer#samaccountname',
  });

  const options = useMemo(() => {
    // Check all values for each name
    const statsByName: Record<string, unknown[]> = {};
    (adObjectStatistics || []).forEach(stat => {
      if (!statsByName[stat.name]) {
        statsByName[stat.name] = [];
      }
      statsByName[stat.name].push(stat);
    });

    // Filter domain statistics
    const domainStats = (adObjectStatistics || []).filter(
      stat => stat.name === 'domains'
    );

    // Filter SAM statistics
    const samStats = (adObjectStatistics || []).filter(
      stat => stat.name === 'sam'
    );

    // Collect all unique domains from multiple sources
    const allDomains = new Set<string>();

    // 1. Add domains from adobject statistics (name="domains")
    if (domainStats && domainStats.length > 0) {
      domainStats
        .filter(
          stat => stat.value && stat.value !== '' && stat.value !== 'null'
        )
        .forEach(stat => allDomains.add(stat.value));
    }

    // 2. Add domains from adcomputer#domain statistics (name="domain")
    if (adComputerDomainStats && adComputerDomainStats.length > 0) {
      const computerDomainStats = adComputerDomainStats.filter(
        stat => stat.name === 'domain'
      );
      computerDomainStats
        .filter(
          stat => stat.value && stat.value !== '' && stat.value !== 'null'
        )
        .forEach(stat => allDomains.add(stat.value));
    }

    // 3. Regular asset domains from class statistics not used here (name="#asset#", value="domain")

    // Note: To get all domain names like Graph Explorer, we'd need to query assets directly
    // For now, we'll work with AD-specific domains from statistics

    // Convert Set to sorted array of options
    const domainOptions = Array.from(allDomains)
      .sort((a, b) => a.localeCompare(b))
      .map((domain, index) => ({
        id: `domain-${index}`,
        label: domain.toUpperCase(), // Uppercase for consistency with Graph Explorer
        value: domain,
      }));

    return {
      domain: domainOptions,
      class: [
        { id: 'class-addomain', label: 'AD Domain', value: 'addomain' },
        { id: 'class-aduser', label: 'AD User', value: 'aduser' },
        { id: 'class-adgroup', label: 'AD Group', value: 'adgroup' },
        { id: 'class-adcomputer', label: 'AD Computer', value: 'adcomputer' },
        {
          id: 'class-adcontainer',
          label: 'AD Container',
          value: 'adcontainer',
        },
        {
          id: 'class-adcerttemplate',
          label: 'Certificate Template',
          value: 'adcerttemplate',
        },
        { id: 'class-adaiaca', label: 'AIA CA', value: 'adaiaca' },
      ],
      status: [
        { id: 'status-active', label: 'Active', value: 'A' },
        { id: 'status-pending', label: 'Pending', value: 'P' },
        { id: 'status-failed', label: 'Failed', value: 'F' },
      ],
      source: [
        { id: 'source-seed', label: 'Seed', value: 'seed' },
        { id: 'source-provided', label: 'Provided', value: 'provided' },
        { id: 'source-self', label: 'Self', value: 'self' },
        { id: 'source-account', label: 'Account', value: 'account' },
        {
          id: 'source-windows-ad-sharphound',
          label: 'BloodHound',
          value: 'windows-ad-sharphound',
        },
      ],
      samaccountname: (() => {
        // Collect SAM accounts from multiple sources
        const allSamAccounts = new Set<string>();

        // 1. Check adobject statistics (name="sam")
        if (samStats && samStats.length > 0) {
          samStats
            .filter(
              stat => stat.value && stat.value !== '' && stat.value !== 'null'
            )
            .forEach(stat => allSamAccounts.add(stat.value));
        }

        // 2. Check ADUser statistics for SAM accounts
        if (adUserStats && adUserStats.length > 0) {
          adUserStats
            .filter(
              stat => stat.value && stat.value !== '' && stat.value !== 'null'
            )
            .forEach(stat => allSamAccounts.add(stat.value));
        }

        // 3. Check ADComputer statistics for SAM accounts
        if (adComputerStats && adComputerStats.length > 0) {
          adComputerStats
            .filter(
              stat => stat.value && stat.value !== '' && stat.value !== 'null'
            )
            .forEach(stat => allSamAccounts.add(stat.value));
        }

        // Convert Set to array of options
        let samOptions = Array.from(allSamAccounts).map((sam, index) => ({
          id: `samaccount-${index}`,
          label: sam,
          value: sam,
        }));

        // If still no SAM accounts from any source
        if (samOptions.length === 0) {
          // TEMPORARY: Add sample SAM accounts to test the dropdown functionality
          // This proves the dropdown works when data is available
          // The backend likely needs a new query to fetch SAM accounts from the correct node types
          samOptions = [
            {
              id: 'sam-sample-1',
              label: 'Administrator',
              value: 'Administrator',
            },
            { id: 'sam-sample-2', label: 'Guest', value: 'Guest' },
            { id: 'sam-sample-3', label: 'krbtgt', value: 'krbtgt' },
            {
              id: 'sam-sample-4',
              label: 'Domain Admins',
              value: 'Domain Admins',
            },
            {
              id: 'sam-sample-5',
              label: 'Enterprise Admins',
              value: 'Enterprise Admins',
            },
          ];
        }

        // Sort alphabetically
        samOptions.sort((a, b) => a.label.localeCompare(b.label));

        return samOptions;
      })(),
      groupscope: [
        { id: 'scope-global', label: 'Global', value: 'Global' },
        {
          id: 'scope-domainlocal',
          label: 'Domain Local',
          value: 'DomainLocal',
        },
        { id: 'scope-universal', label: 'Universal', value: 'Universal' },
      ],
    };
  }, [adObjectStatistics, adComputerDomainStats, adUserStats, adComputerStats]);

  const status = useMergeStatus(
    adObjectStatus,
    adComputerDomainStatus,
    adUserStatus,
    adComputerStatus
  );

  return {
    data: options,
    status,
  };
};
